/**
 * Campaign System - SecaoOfertaTimer Component
 * Componente de seção de oferta completa com preços e benefícios
 */

import { useEffect, useState } from 'react';
import type { Contador, SecaoOfertaTimerProps, TimeLeft } from './types';
import { getContadores, calculateTimeLeft, getOfertaTemaClasses, formatarParcelas } from './utils';

const DEFAULT_STORAGE_KEY = 'campaign_contadores';

export default function SecaoOfertaTimer({
  location = 'secao-oferta',
  storageKey = DEFAULT_STORAGE_KEY,
}: SecaoOfertaTimerProps) {
  const [contador, setContador] = useState<Contador | null>(null);
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);

  useEffect(() => {
    const loadContador = async () => {
      try {
        const contadores = await getContadores(storageKey);

        const ativo = contadores.find(
          (c) => c.ativo && c.location === location
        );

        setContador(ativo || null);
      } catch (error) {
        console.error('Erro ao carregar contador secao-oferta:', error);
        setContador(null);
      }
    };

    loadContador();
  }, [location, storageKey]);

  useEffect(() => {
    if (!contador) {
      setTimeLeft(null);
      return;
    }

    const updateTimer = () => {
      const dataFim = contador.dataFim.toISOString();

      const recorrencia =
        contador.modoRecorrencia === 'sem-recorrencia'
          ? undefined
          : contador.modoRecorrencia;

      const time = calculateTimeLeft(dataFim, recorrencia);
      setTimeLeft(time);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [contador]);

  if (!contador || !timeLeft) {
    return null;
  }

  const tema = getOfertaTemaClasses(contador.tema || 'laranja');
  const textoBotao = contador.textoBotao || 'QUERO ECONOMIZAR';
  const urlBotao = contador.urlBotao || '#contato';

  return (
    <section className="py-16 bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-4xl mx-auto px-6">
        <div className={`${tema.bg} border-2 ${tema.border} rounded-2xl shadow-xl overflow-hidden`}>
          <div className="p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 text-center mb-8">
              {contador.titulo}
            </h2>

            {(contador.precoOriginal || contador.precoAtual) && (
              <div className="text-center mb-8">
                {contador.precoOriginal && (
                  <div className="text-slate-500 text-lg line-through mb-2">
                    De {contador.precoOriginal}
                  </div>
                )}

                {contador.precoAtual && (
                  <div className={`text-5xl md:text-6xl font-bold ${tema.text} mb-2`}>
                    {contador.precoAtual}
                  </div>
                )}

                {contador.precoAtual && (
                  <div className="text-slate-600 text-sm">
                    ou em até 12x de {formatarParcelas(contador.precoAtual, 12)}
                  </div>
                )}
              </div>
            )}

            {contador.beneficios && contador.beneficios.length > 0 && (
              <div className="bg-white rounded-xl p-6 mb-8 shadow-sm">
                <ul className="space-y-3">
                  {contador.beneficios.map((beneficio, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-green-600 text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-slate-700">{beneficio}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className={`${tema.timer} text-white rounded-xl p-6 mb-8`}>
              <div className="text-center mb-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <i className="ri-alarm-warning-line text-2xl"></i>
                  <span className="font-bold text-lg">A Oferta expira em:</span>
                </div>
              </div>

              <div className="flex justify-center gap-3">
                {timeLeft.dias > 0 && (
                  <>
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-3 min-w-[70px] text-center">
                      <div className="text-3xl font-bold leading-none">
                        {String(timeLeft.dias).padStart(2, '0')}
                      </div>
                      <div className="text-xs uppercase leading-none mt-1">Dias</div>
                    </div>
                    <span className="text-3xl font-bold self-center">:</span>
                  </>
                )}

                <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-3 min-w-[70px] text-center">
                  <div className="text-3xl font-bold leading-none">
                    {String(timeLeft.horas).padStart(2, '0')}
                  </div>
                  <div className="text-xs uppercase leading-none mt-1">Horas</div>
                </div>

                <span className="text-3xl font-bold self-center">:</span>

                <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-3 min-w-[70px] text-center">
                  <div className="text-3xl font-bold leading-none">
                    {String(timeLeft.minutos).padStart(2, '0')}
                  </div>
                  <div className="text-xs uppercase leading-none mt-1">Min</div>
                </div>

                <span className="text-3xl font-bold self-center">:</span>

                <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-3 min-w-[70px] text-center">
                  <div className="text-3xl font-bold leading-none">
                    {String(timeLeft.segundos).padStart(2, '0')}
                  </div>
                  <div className="text-xs uppercase leading-none mt-1">Seg</div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <a
                href={urlBotao}
                className={`inline-block ${tema.button} text-white px-8 py-4 rounded-full font-bold text-lg whitespace-nowrap transition-all duration-300 hover:scale-105 shadow-lg cursor-pointer`}
              >
                {textoBotao} <i className="ri-arrow-right-line ml-2"></i>
              </a>

              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-600">
                <i className="ri-shield-check-line text-green-600"></i>
                <span>Garantia de 7 dias</span>
                <span className="mx-2">•</span>
                <i className="ri-lock-line text-green-600"></i>
                <span>Pagamento seguro</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}